import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Button,
  IconButton,
  CircularProgress,
  Alert,
  Container,
} from "@mui/material";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { COLORS, TYPOGRAPHY } from "../../constants/color";
import { Icon } from "../../icons";
import axios from "axios";
import { useAuth } from "../../context/authContext";
import { url } from "../../constants/url";
import AppToast from "../../components/AppToast";

const ViewDPR = () => {
  const { projectId } = useParams();
  const { token, userData } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [project, setProject] = useState(null);
  const [downloading, setDownloading] = useState(false);
  const [selectedDate, setSelectedDate] = useState(null);
  const [groupedDPRData, setGroupedDPRData] = useState({});
  
  // Toast state
  const [toast, setToast] = useState({
    open: false,
    type: "success",
    title: "",
    message: "",
  });

  // Show toast function
  const showToast = (type, title, message) => {
    setToast({
      open: true,
      type,
      title,
      message,
    });
  };

  // Close toast function
  const closeToast = () => {
    setToast({ ...toast, open: false });
  };

  // Check if edit icon should be visible
  const shouldShowEditIcon = () => {
    const userRole = userData?.role;
    const currentDate = new Date().toISOString().split('T')[0];
    const reportDate = selectedDate || location.state?.date || currentDate;
    
    // For admin and superadmin - always visible
    if (userRole === 'admin' || userRole === 'superadmin') {
      return true;
    }
    
    // For engineer - only visible for current date
    if (userRole === 'engineer') {
      return reportDate === currentDate;
    }
    
    // For other roles - not visible
    return false;
  };

  // Format date for display (DD/MM/YYYY)
  const formatDisplayDate = (dateString) => {
    if (!dateString) return "DD/MM/YYYY";
    const date = new Date(dateString);
    return date.toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    });
  };

  // Format date for table (DD MMM YYYY)
  const formatTableDate = (dateString) => {
    if (!dateString) return "-";
    const date = new Date(dateString);
    return date.toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  };

  // Format quantity helper
  const formatQty = (val) => {
    const num = Number(val || 0);
    return String(num).padStart(2, "0");
  };

  // Create proper image URL
  const createImageUrl = (photoPath) => {
    if (!photoPath) {
      return "https://via.placeholder.com/300x200?text=No+Image";
    }
    
    let photoSrc = photoPath;
    
    // Handle both absolute and relative paths
    if (photoPath.startsWith('http')) {
      photoSrc = photoPath;
    } else {
      const cleanPath = photoPath.replace(/^[\\\/]+/, '');
      photoSrc = `${url}/${cleanPath}`;
    }
    
    // Add cache busting parameter
    photoSrc += (photoSrc.includes('?') ? '&' : '?') + `t=${Date.now()}`;
    
    return photoSrc;
  };

  // Fetch project and DPR data
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        setError(null);

        // Get date from location state or use today's date
        const date = location.state?.date || new Date().toISOString().split('T')[0];
        setSelectedDate(date);

        const config = {
          headers: {
            Authorization: token ? `Bearer ${token}` : undefined,
          },
        };

        // Fetch project
        const projectResponse = await axios.get(
          `${url}/api/inhouse-projects/fetch-by/${projectId}`,
          config
        );

        if (projectResponse.data.project) {
          setProject(projectResponse.data.project);

          // Fetch DPR data for the specific date
          const dprResponse = await axios.get(
            `${url}/api/dpr/fetch`,
            {
              ...config,
              params: {
                projectId: projectId,
                reportDate: date,
                page: 1,
                limit: 100
              }
            }
          );

          if (dprResponse.data.dprs && dprResponse.data.dprs.length > 0) {
            // Group DPR data by report type
            const grouped = {};
            dprResponse.data.dprs.forEach(dprGroup => {
              if (dprGroup.entries && dprGroup.entries.length > 0) {
                if (!grouped[dprGroup.reportType]) {
                  grouped[dprGroup.reportType] = [];
                }
                grouped[dprGroup.reportType].push(...dprGroup.entries);
              }
            });
            setGroupedDPRData(grouped);
          } else {
            setError("No DPR data found for this date");
          }
        } else {
          setError("Project not found");
        }
      } catch (err) {
        console.error("Error fetching data:", err);
        setError("Failed to load DPR data. Please try again.");
      } finally {
        setLoading(false);
      }
    };

    if (projectId && token) {
      fetchData();
    }
  }, [projectId, token, location.state]);

  // Download DPR Report via API
  const handleDownloadDPRReport = async () => {
    if (!selectedDate) return;
    
    try {
      setDownloading(true);
      
      const config = {
        headers: {
          Authorization: token ? `Bearer ${token}` : undefined,
        },
        responseType: 'blob'
      };
      
      const response = await axios.get(
        `${url}/api/dpr/download-report/${projectId}`,
        {
          ...config,
          params: {
            reportDate: selectedDate
          }
        }
      );
      
      // Create blob and download
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const urlBlob = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = urlBlob;
      
      // Generate filename
      const dateObj = new Date(selectedDate);
      const dateStr = dateObj.toLocaleDateString('en-GB').replace(/\//g, '-');
      const projectName = project?.projectName?.replace(/[^a-zA-Z0-9]/g, '_') || 'DPR';
      link.download = `DPR_${projectName}_${dateStr}.pdf`;
      
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(urlBlob);
      
      showToast("success", "Success", "DPR report downloaded successfully!");
      
    } catch (err) {
      console.error("Error downloading DPR report:", err);
      const errorMessage = err.response?.data?.message || "Failed to download DPR report. Please try again.";
      showToast("error", "Error", errorMessage);
    } finally {
      setDownloading(false);
    }
  };

  // Navigate to edit DPR screen
  const handleEditDPR = () => {
    navigate(`/progress/dpr/edit/${projectId}`, {
      state: {
        date: selectedDate,
        dprId: location.state?.dprId
      }
    });
  };

  // Helper function to render labour data (same as edit screen)
  const renderLabourData = (labourMap) => {
    if (!labourMap || typeof labourMap !== "object") {
      return null;
    }

    const entries = Object.entries(labourMap);
    if (entries.length === 0) {
      return null;
    }

    return entries.map(([type, qty]) => (
      <Typography
        key={type}
        sx={{ ...TYPOGRAPHY.BODY_LARGE, color: COLORS.SECONDARY, fontSize: "14px" }}
      >
        {type} - {formatQty(qty)}
      </Typography>
    ));
  };

  // Helper to check if data exists
  const hasValidData = (array, dataType = "default") => {
    if (!array || !Array.isArray(array) || array.length === 0) {
      return false;
    }

    return array.some(item => {
      switch (dataType) {
        case "workProgress":
          return item.workProgressDetails?.workDescription?.trim() !== "";
        case "materialReceived":
          return item.materialDescription?.trim() !== "";
        case "machineryReport":
          return item.machineryDetails?.equipmentName?.trim() !== "";
        case "qualityControl":
          return item.qualityControlDetails?.testDescription?.trim() !== "";
        case "safetyObservations":
          return item.safetyObservationDetails?.itemName?.trim() !== "";
        case "visitorLogs":
          return item.visitorLogDetails?.visitorName?.trim() !== "";
        case "delaysHindrances":
          return item.delayHindrancesDetails?.descriptionOfDelay?.trim() !== "";
        case "photographs":
          const details = item.photographDetails;
          return details && (details.activityLocation || details.photoPath);
        default:
          return true;
      }
    });
  };

  // Define all sections
  const sections = [
    {
      key: "workProgress",
      title: "Work Progress & Manpower Deployment",
      data: groupedDPRData["Work Progress & Manpower Deployment"] || [],
      dataType: "workProgress"
    },
    {
      key: "materials",
      title: "Material Received Report",
      data: groupedDPRData["Material Received Report"] || [],
      dataType: "materialReceived"
    },
    {
      key: "machinery",
      title: "Machinery Report",
      data: groupedDPRData["Machinery Report"] || [],
      dataType: "machineryReport"
    },
    {
      key: "qualityControl",
      title: "Quality Control & Testing",
      data: groupedDPRData["Quality Control & Testing"] || [],
      dataType: "qualityControl"
    },
    {
      key: "safetyObservations",
      title: "Safety Observations",
      data: groupedDPRData["Safety Observation"] || [],
      dataType: "safetyObservations"
    },
    {
      key: "visitorLogs",
      title: "Visitor Logs",
      data: groupedDPRData["Visitor Log"] || [],
      dataType: "visitorLogs"
    },
    {
      key: "delaysHindrances",
      title: "Delays & Hindrances",
      data: groupedDPRData["Delay Hindrances"] || [],
      dataType: "delaysHindrances"
    },
    {
      key: "photographs",
      title: "Photographs",
      data: groupedDPRData["Photograph"] || [],
      dataType: "photographs"
    }
  ];

  // ========================= WORK PROGRESS SECTION =========================
  const renderWorkProgressSection = () => {
    const data = groupedDPRData["Work Progress & Manpower Deployment"] || [];
    if (!hasValidData(data, "workProgress")) return null;

    // Calculate totals
    let totalSkilledLabour = {};
    let totalUnskilledLabour = {};
    let totalQty = 0;

    data.forEach(entry => {
      const details = entry.workProgressDetails || {};
      
      // Sum quantities
      totalQty += Number(details.estimatedQuantity || 0);

      // Sum skilled labour
      const skilledLabour = details.skilledLabour || {};
      Object.entries(skilledLabour).forEach(([type, qty]) => {
        const quantity = Number(qty || 0);
        totalSkilledLabour[type] = (totalSkilledLabour[type] || 0) + quantity;
      });

      // Sum unskilled labour
      const unskilledLabour = details.unskilledLabour || {};
      Object.entries(unskilledLabour).forEach(([type, qty]) => {
        const quantity = Number(qty || 0);
        totalUnskilledLabour[type] = (totalUnskilledLabour[type] || 0) + quantity;
      });
    });

    const totalSkilled = Object.entries(totalSkilledLabour).map(([type, qty]) => `${type} - ${qty}`);
    const totalUnskilled = Object.entries(totalUnskilledLabour).map(([type, qty]) => `${type} - ${qty}`);

    return (
      <Box mt={4}>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
          <Typography sx={{ ...TYPOGRAPHY.H3, color: COLORS.TEXT }}>
            Work Progress & Manpower Deployment
          </Typography>
        </Box>

        {/* TABLE - Same UI as edit screen */}
        <Box>
          {/* HEADER ROW */}
          <FlexRow
            sx={{
              borderBottom: `1px solid ${COLORS.LIGHT_SECONDARY}`,
              pb: 1,
            }}
          >
            <FlexCell w="40px" sx={{ pl: 1 }}>
              <Typography sx={{ ...TYPOGRAPHY.H4, color: COLORS.TEXT }}>
                Sr.No.
              </Typography>
            </FlexCell>

            <FlexCell flex={2} sx={{ pl: 2 }}>
              <Typography sx={{ ...TYPOGRAPHY.H4, color: COLORS.TEXT }}>
                Work Description
              </Typography>
            </FlexCell>

            <FlexCell flex={1.5} sx={{ pl: 2 }}>
              <Typography sx={{ ...TYPOGRAPHY.H4, color: COLORS.TEXT }}>
                Contractor Name
              </Typography>
            </FlexCell>

            <FlexCell flex={1.5} sx={{ pl: 2 }}>
              <Typography sx={{ ...TYPOGRAPHY.H4, color: COLORS.TEXT }}>
                Skilled Labour
              </Typography>
            </FlexCell>

            <FlexCell flex={1.2} sx={{ pl: 2 }}>
              <Typography sx={{ ...TYPOGRAPHY.H4, color: COLORS.TEXT }}>
                Unskilled Labour
              </Typography>
            </FlexCell>

            <FlexCell w="60px" sx={{ pl: 2 }}>
              <Typography sx={{ ...TYPOGRAPHY.H4, color: COLORS.TEXT }}>
                Est. QTY
              </Typography>
            </FlexCell>

            <FlexCell w="80px" sx={{ pl: 2 }}>
              <Typography sx={{ ...TYPOGRAPHY.H4, color: COLORS.TEXT }}>
                Unit
              </Typography>
            </FlexCell>

            <FlexCell flex={1.2} sx={{ pl: 2 }}>
              <Typography sx={{ ...TYPOGRAPHY.H4, color: COLORS.TEXT }}>
                Remark
              </Typography>
            </FlexCell>
          </FlexRow>

          {/* DATA ROWS */}
          {data.length === 0 ? (
            <Box sx={{ py: 3, textAlign: 'center', borderBottom: `1px solid ${COLORS.LIGHT_SECONDARY}` }}>
              <Typography sx={{ ...TYPOGRAPHY.BODY_LARGE, color: COLORS.TEXT_SECONDARY }}>
                No work progress entries found.
              </Typography>
            </Box>
          ) : (
            data.map((row, i) => {
              const details = row.workProgressDetails || {};
              const skilledLabour = details.skilledLabour || {};
              const unskilledLabour = details.unskilledLabour || {};

              return (
                <FlexRow
                  key={row._id}
                  sx={{
                    borderBottom: `1px solid ${COLORS.LIGHT_SECONDARY}`,
                    py: 1,
                  }}
                >
                  <FlexCell w="40px" sx={{ pl: 1 }}>
                    <Typography sx={{ ...TYPOGRAPHY.BODY_LARGE, color: COLORS.SECONDARY }}>
                      {i + 1}.
                    </Typography>
                  </FlexCell>

                  <FlexCell flex={2} sx={{ pl: 2 }}>
                    <Typography sx={{
                      ...TYPOGRAPHY.BODY_LARGE,
                      color: COLORS.SECONDARY,
                      wordWrap: "break-word",
                      whiteSpace: "normal",
                      lineHeight: 1.4
                    }}>
                      {details.workDescription || "-"}
                    </Typography>
                  </FlexCell>

                  <FlexCell flex={1.5} sx={{ pl: 2 }}>
                    <Typography sx={{ ...TYPOGRAPHY.BODY_LARGE, color: COLORS.SECONDARY }}>
                      {details.contractorName || "-"}
                    </Typography>
                  </FlexCell>

                  <FlexCell flex={1.5} sx={{ pl: 2 }}>
                    {Object.entries(skilledLabour).length > 0 ? (
                      Object.entries(skilledLabour).map(([type, qty]) => (
                        <Typography
                          key={type}
                          sx={{ ...TYPOGRAPHY.BODY_LARGE, color: COLORS.SECONDARY }}
                        >
                          {type} - {formatQty(qty)}
                        </Typography>
                      ))
                    ) : (
                      <Typography sx={{ ...TYPOGRAPHY.BODY_LARGE, color: COLORS.SECONDARY, fontStyle: "italic" }}>
                        -
                      </Typography>
                    )}
                  </FlexCell>

                  <FlexCell flex={1.2} sx={{ pl: 2 }}>
                    {Object.entries(unskilledLabour).length > 0 ? (
                      Object.entries(unskilledLabour).map(([type, qty]) => (
                        <Typography
                          key={type}
                          sx={{ ...TYPOGRAPHY.BODY_LARGE, color: COLORS.SECONDARY }}
                        >
                          {type} - {formatQty(qty)}
                        </Typography>
                      ))
                    ) : (
                      <Typography sx={{ ...TYPOGRAPHY.BODY_LARGE, color: COLORS.SECONDARY, fontStyle: "italic" }}>
                        -
                      </Typography>
                    )}
                  </FlexCell>

                  <FlexCell w="60px" sx={{ pl: 2 }}>
                    <Typography sx={{ ...TYPOGRAPHY.BODY_LARGE, color: COLORS.SECONDARY }}>
                      {formatQty(details.estimatedQuantity || 0)}
                    </Typography>
                  </FlexCell>

                  <FlexCell w="80px" sx={{ pl: 2 }}>
                    <Typography sx={{ ...TYPOGRAPHY.BODY_LARGE, color: COLORS.SECONDARY }}>
                      {details.unit && details.unit.trim() !== "" ? details.unit : "Person"}
                    </Typography>
                  </FlexCell>

                  <FlexCell flex={1.2} sx={{ pl: 2 }}>
                    <Typography sx={{ ...TYPOGRAPHY.BODY_LARGE, color: COLORS.SECONDARY }}>
                      {details.remarks || row.adminRemarks || "-"}
                    </Typography>
                  </FlexCell>
                </FlexRow>
              );
            })
          )}

          {/* TOTAL ROW - Only show if there are rows */}
          {data.length > 0 && (
            <FlexRow
              sx={{
                borderTop: `1px solid ${COLORS.LIGHT_SECONDARY}`,
                py: 1,
                backgroundColor: '#f8f9fa'
              }}
            >
              <FlexCell w="40px" sx={{ pl: 1 }} />
              <FlexCell flex={2} sx={{ pl: 2 }} />
              <FlexCell flex={1.5} sx={{ pl: 2 }}>
                <Typography sx={{ ...TYPOGRAPHY.BODY_LARGE, color: COLORS.PRIMARY, fontWeight: 600 }}>
                  Total
                </Typography>
              </FlexCell>

              <FlexCell flex={1.5} sx={{ pl: 2 }}>
                {totalSkilled.map((s, index) => (
                  <Typography
                    key={index}
                    sx={{ ...TYPOGRAPHY.BODY_LARGE, color: COLORS.PRIMARY, fontWeight: 600 }}
                  >
                    {s}
                  </Typography>
                ))}
              </FlexCell>

              <FlexCell flex={1.2} sx={{ pl: 2 }}>
                {totalUnskilled.map((u, index) => (
                  <Typography
                    key={index}
                    sx={{ ...TYPOGRAPHY.BODY_LARGE, color: COLORS.PRIMARY, fontWeight: 600 }}
                  >
                    {u}
                  </Typography>
                ))}
              </FlexCell>

              <FlexCell w="60px" sx={{ pl: 2 }}>
                <Typography sx={{ ...TYPOGRAPHY.BODY_LARGE, color: COLORS.PRIMARY, fontWeight: 600 }}>
                  {formatQty(totalQty)}
                </Typography>
              </FlexCell>

              <FlexCell w="80px" sx={{ pl: 2 }} />
              <FlexCell flex={1.2} sx={{ pl: 2 }} />
            </FlexRow>
          )}
        </Box>
      </Box>
    );
  };

  // ========================= MATERIAL RECEIVED SECTION =========================
  const renderMaterialsSection = () => {
    const data = groupedDPRData["Material Received Report"] || [];
    if (!hasValidData(data, "materialReceived")) return null;

    return (
      <Box mt={4}>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
          <Typography sx={{ ...TYPOGRAPHY.H3, color: COLORS.TEXT, mb: 1.5 }}>
            Material Received Report
          </Typography>
        </Box>

        {/* HEADER */}
        <FlexRow sx={{ borderBottom: `1px solid ${COLORS.LIGHT_SECONDARY}`, pb: 1 }}>
          <FlexCell w="50px">
            <Typography sx={{ ...TYPOGRAPHY.H4, color: COLORS.TEXT }}>Sr.No.</Typography>
          </FlexCell>
          <FlexCell flex={2} pl={1}>
            <Typography sx={{ ...TYPOGRAPHY.H4, color: COLORS.TEXT }}>Material Description</Typography>
          </FlexCell>
          <FlexCell flex={1.5}>
            <Typography sx={{ ...TYPOGRAPHY.H4, color: COLORS.TEXT }}>Supplier Name</Typography>
          </FlexCell>
          <FlexCell flex={1.2}>
            <Typography sx={{ ...TYPOGRAPHY.H4, color: COLORS.TEXT }}>Challan No</Typography>
          </FlexCell>
          <FlexCell w="60px">
            <Typography sx={{ ...TYPOGRAPHY.H4, color: COLORS.TEXT }}>Unit</Typography>
          </FlexCell>
          <FlexCell flex={2}>
            <Typography sx={{ ...TYPOGRAPHY.H4, color: COLORS.TEXT }}>Details</Typography>
          </FlexCell>
          <FlexCell flex={1.2}>
            <Typography sx={{ ...TYPOGRAPHY.H4, color: COLORS.TEXT }}>Remark</Typography>
          </FlexCell>
        </FlexRow>

        {/* ROWS */}
        {data.map((row, index) => {
          const details = row.materialDetails || {};
          const opening = details.openingBalance || 0;
          const received = details.receivedToday || 0;
          const total = details.totalReceived || Number(opening) + Number(received);
          const consumed = details.consumedToday || 0;
          const closing = details.closingBalance || Number(total) - Number(consumed);

          return (
            <FlexRow
              key={row._id}
              sx={{
                borderBottom: `1px solid ${COLORS.LIGHT_SECONDARY}`,
                py: 1,
              }}
            >
              <FlexCell w="50px">
                <Typography sx={{ ...TYPOGRAPHY.BODY_LARGE, color: COLORS.SECONDARY }}>
                  {index + 1}.
                </Typography>
              </FlexCell>

              <FlexCell flex={2} pl={1}>
                <Typography sx={{
                  ...TYPOGRAPHY.BODY_LARGE,
                  color: COLORS.SECONDARY,
                  wordWrap: "break-word",
                  whiteSpace: "normal",
                  lineHeight: 1.4
                }}>
                  {row.materialDescription || "-"}
                </Typography>
              </FlexCell>

              <FlexCell flex={1.5}>
                <Typography sx={{ ...TYPOGRAPHY.BODY_LARGE, color: COLORS.SECONDARY }}>
                  {row.supplierName || "-"}
                </Typography>
              </FlexCell>

              <FlexCell flex={1.2}>
                <Typography sx={{ ...TYPOGRAPHY.BODY_LARGE, color: COLORS.SECONDARY }}>
                  {row.challanNumber || "-"}
                </Typography>
              </FlexCell>

              <FlexCell w="60px">
                <Typography sx={{ ...TYPOGRAPHY.BODY_LARGE, color: COLORS.SECONDARY }}>
                  {row.unit || "-"}
                </Typography>
              </FlexCell>

              {/* DETAILS */}
              <FlexCell flex={2}>
                {[
                  ["Opening Balance", opening],
                  ["Received Today", received],
                  ["Total Received", total],
                  ["Consumed Today", consumed],
                  ["Closing Balance", closing],
                ].map(([label, value], index, arr) => (
                  <Box
                    key={label}
                    sx={{
                      borderBottom: index !== arr.length - 1 ? `1px solid ${COLORS.LIGHT_SECONDARY}` : "none",
                      pb: 0.4,
                      mb: 0.4,
                      pr: 2,
                      width: "78%",
                    }}
                  >
                    <Typography sx={{ ...TYPOGRAPHY.BODY_LARGE, color: COLORS.SECONDARY, display: "flex", gap: 2.3 }}>
                      <span>{label}</span>
                      <span>{formatQty(value)}</span>
                    </Typography>
                  </Box>
                ))}
              </FlexCell>

              <FlexCell flex={1.2}>
                <Typography sx={{ ...TYPOGRAPHY.BODY_LARGE, color: COLORS.SECONDARY }}>
                  {row.adminRemarks || "-"}
                </Typography>
              </FlexCell>
            </FlexRow>
          );
        })}
      </Box>
    );
  };

  // ========================= MACHINERY REPORT SECTION =========================
  const renderMachinerySection = () => {
    const data = groupedDPRData["Machinery Report"] || [];
    if (!hasValidData(data, "machineryReport")) return null;

    return (
      <Box mt={4}>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
          <Typography sx={{ ...TYPOGRAPHY.H3, color: COLORS.TEXT, mb: 1.5 }}>
            Machinery Report
          </Typography>
        </Box>

        {/* HEADER */}
        <FlexRow sx={{ borderBottom: `1px solid ${COLORS.LIGHT_SECONDARY}`, pb: 1 }}>
          <FlexCell w="40px">
            <Typography sx={{ ...TYPOGRAPHY.H4, color: COLORS.TEXT }}>Sr.No.</Typography>
          </FlexCell>
          <FlexCell flex={1.5}>
            <Typography sx={{ ...TYPOGRAPHY.H4, color: COLORS.TEXT }}>Equipment Name</Typography>
          </FlexCell>
          <FlexCell flex={1.5}>
            <Typography sx={{ ...TYPOGRAPHY.H4, color: COLORS.TEXT }}>Supplier Name</Typography>
          </FlexCell>
          <FlexCell flex={2}>
            <Typography sx={{ ...TYPOGRAPHY.H4, color: COLORS.TEXT }}>Time</Typography>
          </FlexCell>
          <FlexCell w="90px">
            <Typography sx={{ ...TYPOGRAPHY.H4, color: COLORS.TEXT }}>Planned Qty</Typography>
          </FlexCell>
          <FlexCell w="90px">
            <Typography sx={{ ...TYPOGRAPHY.H4, color: COLORS.TEXT }}>Actual Qty</Typography>
          </FlexCell>
          <FlexCell flex={1.4}>
            <Typography sx={{ ...TYPOGRAPHY.H4, color: COLORS.TEXT }}>Remark</Typography>
          </FlexCell>
        </FlexRow>

        {/* ROWS */}
        {data.map((row, index) => {
          const details = row.machineryDetails || {};

          return (
            <FlexRow sx={{ borderBottom: `1px solid ${COLORS.LIGHT_SECONDARY}`, py: 1.2 }} key={row._id}>
              <FlexCell w="40px">
                <Typography sx={{ ...TYPOGRAPHY.BODY_LARGE, color: COLORS.SECONDARY }}>{index + 1}.</Typography>
              </FlexCell>

              <FlexCell flex={1.5}>
                <Typography sx={{ ...TYPOGRAPHY.BODY_LARGE, color: COLORS.SECONDARY }}>
                  {details.equipmentName || "-"}
                </Typography>
              </FlexCell>

              <FlexCell flex={1.5}>
                <Typography sx={{ ...TYPOGRAPHY.BODY_LARGE, color: COLORS.SECONDARY }}>{details.supplierName || "-"}</Typography>
              </FlexCell>

              <FlexCell flex={2}>
                {(Number(details.startReading) > 0 || Number(details.endReading) > 0) && ((details.inTime !== "" && details.inTime !== "-") || (details.outTime !== "" && details.outTime !== "-")) ? (
                  <>
                    <Typography sx={{ ...TYPOGRAPHY.BODY_LARGE, color: COLORS.SECONDARY }}>
                      Start Reading: <span style={{ color: COLORS.PRIMARY }}>{details.startReading || "0"}</span>
                    </Typography>
                    <Typography sx={{ ...TYPOGRAPHY.BODY_LARGE, color: COLORS.SECONDARY }}>
                      End Reading: <span style={{ color: COLORS.PRIMARY }}>{details.endReading || "0"}</span>
                    </Typography>
                    <Typography sx={{ ...TYPOGRAPHY.BODY_LARGE, color: COLORS.SECONDARY }}>
                      In Time: <span style={{ color: COLORS.PRIMARY }}>{details.inTime}</span>
                    </Typography>
                    <Typography sx={{ ...TYPOGRAPHY.BODY_LARGE, color: COLORS.SECONDARY }}>
                      Out Time: <span style={{ color: COLORS.PRIMARY }}>{details.outTime}</span>
                    </Typography>
                  </>
                ) : (Number(details.startReading) > 0 || Number(details.endReading) > 0) ? (
                  <>
                    <Typography sx={{ ...TYPOGRAPHY.BODY_LARGE, color: COLORS.SECONDARY }}>
                      Start Reading: <span style={{ color: COLORS.PRIMARY }}>{details.startReading || "0"}</span>
                    </Typography>
                    <Typography sx={{ ...TYPOGRAPHY.BODY_LARGE, color: COLORS.SECONDARY }}>
                      End Reading: <span style={{ color: COLORS.PRIMARY }}>{details.endReading || "0"}</span>
                    </Typography>
                  </>
                ) : ((details.inTime !== "" && details.inTime !== "-") || (details.outTime !== "" && details.outTime !== "-")) ? (
                  <>
                    <Typography sx={{ ...TYPOGRAPHY.BODY_LARGE, color: COLORS.SECONDARY }}>
                      In Time: <span style={{ color: COLORS.PRIMARY }}>{details.inTime}</span>
                    </Typography>
                    <Typography sx={{ ...TYPOGRAPHY.BODY_LARGE, color: COLORS.SECONDARY }}>
                      Out Time: <span style={{ color: COLORS.PRIMARY }}>{details.outTime}</span>
                    </Typography>
                  </>
                ) : (
                  <>
                    <Typography sx={{ ...TYPOGRAPHY.BODY_LARGE, color: COLORS.SECONDARY }}>
                      Start Reading: <span style={{ color: COLORS.PRIMARY }}>{details.startReading || "0"}</span>
                    </Typography>
                    <Typography sx={{ ...TYPOGRAPHY.BODY_LARGE, color: COLORS.SECONDARY }}>
                      End Reading: <span style={{ color: COLORS.PRIMARY }}>{details.endReading || "0"}</span>
                    </Typography>
                    <Typography sx={{ ...TYPOGRAPHY.BODY_LARGE, color: COLORS.SECONDARY }}>
                      In Time: <span style={{ color: COLORS.PRIMARY }}>{(details.inTime === "" || details.inTime === "-") ? "-" : details.inTime}</span>
                    </Typography>
                    <Typography sx={{ ...TYPOGRAPHY.BODY_LARGE, color: COLORS.SECONDARY }}>
                      Out Time: <span style={{ color: COLORS.PRIMARY }}>{(details.outTime === "" || details.outTime === "-") ? "-" : details.outTime}</span>
                    </Typography>
                  </>
                )}
                <Typography sx={{ ...TYPOGRAPHY.BODY_LARGE, color: COLORS.SECONDARY }}>
                  Working: <span style={{ color: COLORS.PRIMARY }}>{details.workingHours || "-"}</span>
                </Typography>
              </FlexCell>

              <FlexCell w="90px">
                <Typography sx={{ ...TYPOGRAPHY.BODY_LARGE, color: COLORS.SECONDARY }}>{String(details.plannedQty || 0).padStart(2, "0")}</Typography>
              </FlexCell>

              <FlexCell w="90px">
                <Typography sx={{ ...TYPOGRAPHY.BODY_LARGE, color: COLORS.SECONDARY }}>{String(details.actualQty || 0).padStart(2, "0")}</Typography>
              </FlexCell>

              <FlexCell flex={1.4}>
                <Typography sx={{ ...TYPOGRAPHY.BODY_LARGE, color: COLORS.SECONDARY }}>{details.remarks || row.adminRemarks || "-"}</Typography>
              </FlexCell>
            </FlexRow>
          );
        })}
      </Box>
    );
  };

  // ========================= QUALITY CONTROL SECTION =========================
  const renderQualityControlSection = () => {
    const data = groupedDPRData["Quality Control & Testing"] || [];
    if (!hasValidData(data, "qualityControl")) return null;

    return (
      <Box mt={4}>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
          <Typography sx={{ ...TYPOGRAPHY.H3, color: COLORS.TEXT, mb: 1 }}>
            Quality Control & Testing
          </Typography>
        </Box>

        {/* TABLE */}
        <Box>
          {/* HEADER */}
          <FlexRow>
            <FlexCell w="40px">
              <Typography sx={{ ...TYPOGRAPHY.H4, color: COLORS.TEXT }}>
                Sr
              </Typography>
            </FlexCell>

            <FlexCell flex={2}>
              <Typography sx={{ ...TYPOGRAPHY.H4, color: COLORS.TEXT }}>
                Test Description
              </Typography>
            </FlexCell>

            <FlexCell flex={1.5}>
              <Typography sx={{ ...TYPOGRAPHY.H4, color: COLORS.TEXT }}>
                Location / Element
              </Typography>
            </FlexCell>

            <FlexCell w="120px">
              <Typography sx={{ ...TYPOGRAPHY.H4, color: COLORS.TEXT }}>
                Date
              </Typography>
            </FlexCell>

            <FlexCell w="80px">
              <Typography sx={{ ...TYPOGRAPHY.H4, color: COLORS.TEXT }}>
                No of Test
              </Typography>
            </FlexCell>

            <FlexCell flex={1.5}>
              <Typography sx={{ ...TYPOGRAPHY.H4, color: COLORS.TEXT }}>
                Results
              </Typography>
            </FlexCell>

            <FlexCell w="100px">
              <Typography sx={{ ...TYPOGRAPHY.H4, color: COLORS.TEXT }}>
                Status
              </Typography>
            </FlexCell>

            <FlexCell flex={1.5}>
              <Typography sx={{ ...TYPOGRAPHY.H4, color: COLORS.TEXT }}>
                Remark
              </Typography>
            </FlexCell>
          </FlexRow>

          {/* ROWS */}
          {data.map((row, i) => {
            const details = row.qualityControlDetails || {};

            return (
              <FlexRow key={row._id}>
                <FlexCell w="40px">
                  <Typography sx={{ ...TYPOGRAPHY.BODY_LARGE, color: COLORS.SECONDARY }}>
                    {i + 1}
                  </Typography>
                </FlexCell>

                <FlexCell flex={2}>
                  <Typography sx={{ ...TYPOGRAPHY.BODY_LARGE, color: COLORS.SECONDARY }}>
                    {details.testDescription || "-"}
                  </Typography>
                </FlexCell>

                <FlexCell flex={1.5}>
                  <Typography sx={{ ...TYPOGRAPHY.BODY_LARGE, color: COLORS.SECONDARY }}>
                    {details.locationElement || "-"}
                  </Typography>
                </FlexCell>

                <FlexCell w="120px">
                  <Typography sx={{ ...TYPOGRAPHY.BODY_LARGE, color: COLORS.SECONDARY }}>
                    {formatTableDate(details.testDate)}
                  </Typography>
                </FlexCell>

                <FlexCell w="80px">
                  <Typography sx={{ ...TYPOGRAPHY.BODY_LARGE, color: COLORS.SECONDARY }}>
                    {details.numberOfTests || 0}
                  </Typography>
                </FlexCell>

                <FlexCell flex={1.5}>
                  <Typography sx={{ ...TYPOGRAPHY.BODY_LARGE, color: COLORS.SECONDARY }}>
                    {details.results || "-"}
                  </Typography>
                </FlexCell>

                <FlexCell w="100px">
                  <Typography sx={{ ...TYPOGRAPHY.BODY_LARGE, color: COLORS.SECONDARY }}>
                    {details.status || "-"}
                  </Typography>
                </FlexCell>

                <FlexCell flex={1.5}>
                  <Typography sx={{ ...TYPOGRAPHY.BODY_LARGE, color: COLORS.SECONDARY }}>
                    {details.remarks || row.adminRemarks || "-"}
                  </Typography>
                </FlexCell>
              </FlexRow>
            );
          })}
        </Box>
      </Box>
    );
  };

  // ========================= SAFETY OBSERVATIONS SECTION =========================
  const renderSafetyObservationsSection = () => {
    const data = groupedDPRData["Safety Observation"] || [];
    if (!hasValidData(data, "safetyObservations")) return null;

    return (
      <Box mt={4}>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
          <Typography sx={{ ...TYPOGRAPHY.H3, color: COLORS.TEXT, mb: 1 }}>
            Safety Observations
          </Typography>
        </Box>

        {/* TABLE */}
        <Box>
          {/* HEADER */}
          <FlexRow>
            <FlexCell w="40px">
              <Typography sx={{ ...TYPOGRAPHY.H4, color: COLORS.TEXT }}>
                Sr
              </Typography>
            </FlexCell>

            <FlexCell flex={2}>
              <Typography sx={{ ...TYPOGRAPHY.H4, color: COLORS.TEXT }}>
                Item
              </Typography>
            </FlexCell>

            <FlexCell w="120px">
              <Typography sx={{ ...TYPOGRAPHY.H4, color: COLORS.TEXT }}>
                Status
              </Typography>
            </FlexCell>

            <FlexCell flex={2}>
              <Typography sx={{ ...TYPOGRAPHY.H4, color: COLORS.TEXT }}>
                Remark
              </Typography>
            </FlexCell>
          </FlexRow>

          {/* ROWS */}
          {data.map((row, i) => {
            const details = row.safetyObservationDetails || {};

            return (
              <FlexRow key={row._id}>
                <FlexCell w="40px">
                  <Typography sx={{ ...TYPOGRAPHY.BODY_LARGE, color: COLORS.SECONDARY }}>
                    {i + 1}
                  </Typography>
                </FlexCell>

                <FlexCell flex={2}>
                  <Typography sx={{ ...TYPOGRAPHY.BODY_LARGE, color: COLORS.SECONDARY }}>
                    {details.itemName || "-"}
                  </Typography>
                </FlexCell>

                <FlexCell w="120px">
                  <Typography sx={{ ...TYPOGRAPHY.BODY_LARGE, color: COLORS.SECONDARY }}>
                    {details.status || "-"}
                  </Typography>
                </FlexCell>

                <FlexCell flex={2}>
                  <Typography sx={{ ...TYPOGRAPHY.BODY_LARGE, color: COLORS.SECONDARY }}>
                    {details.remarks || row.adminRemarks || "-"}
                  </Typography>
                </FlexCell>
              </FlexRow>
            );
          })}
        </Box>
      </Box>
    );
  };

  // ========================= VISITOR LOGS SECTION =========================
  const renderVisitorLogsSection = () => {
    const data = groupedDPRData["Visitor Log"] || [];
    if (!hasValidData(data, "visitorLogs")) return null;

    return (
      <Box mt={4}>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
          <Typography sx={{ ...TYPOGRAPHY.H3, color: COLORS.TEXT, mb: 1 }}>
            Visitor Logs
          </Typography>
        </Box>

        {/* HEADER */}
        <FlexRow sx={{ borderBottom: `1px solid ${COLORS.LIGHT_SECONDARY}`, pb: 1 }}>
          <FlexCell w="40px">
            <Typography sx={{ ...TYPOGRAPHY.H4, color: COLORS.TEXT }}>Sr</Typography>
          </FlexCell>
          <FlexCell flex={1.5}>
            <Typography sx={{ ...TYPOGRAPHY.H4, color: COLORS.TEXT }}>Visitor Name</Typography>
          </FlexCell>
          <FlexCell flex={1.2}>
            <Typography sx={{ ...TYPOGRAPHY.H4, color: COLORS.TEXT }}>Purpose of Visit</Typography>
          </FlexCell>
          <FlexCell flex={1.5}>
            <Typography sx={{ ...TYPOGRAPHY.H4, color: COLORS.TEXT }}>Instruction / Decisions</Typography>
          </FlexCell>
          <FlexCell w="90px">
            <Typography sx={{ ...TYPOGRAPHY.H4, color: COLORS.TEXT }}>Time In</Typography>
          </FlexCell>
          <FlexCell w="90px">
            <Typography sx={{ ...TYPOGRAPHY.H4, color: COLORS.TEXT }}>Time Out</Typography>
          </FlexCell>
          <FlexCell flex={1.5}>
            <Typography sx={{ ...TYPOGRAPHY.H4, color: COLORS.TEXT }}>Remark</Typography>
          </FlexCell>
        </FlexRow>

        {/* ROWS */}
        {data.map((row, i) => {
          const details = row.visitorLogDetails || {};

          return (
            <FlexRow sx={{ borderBottom: `1px solid ${COLORS.LIGHT_SECONDARY}`, py: 1 }} key={row._id}>
              <FlexCell w="40px">
                <Typography sx={{ ...TYPOGRAPHY.BODY_LARGE, color: COLORS.SECONDARY }}>
                  {i + 1}
                </Typography>
              </FlexCell>

              <FlexCell flex={1.5}>
                <Box display="flex" flexDirection="column">
                  <Typography sx={{ ...TYPOGRAPHY.BODY_LARGE, color: COLORS.SECONDARY }}>
                    {details.visitorName || "-"}
                  </Typography>
                  <Typography sx={{ ...TYPOGRAPHY.BODY_SMALL, color: COLORS.TEXT_SECONDARY, lineHeight: 1.3 }}>
                    {details.companyName || "-"}
                  </Typography>
                  <Typography sx={{ ...TYPOGRAPHY.BODY_SMALL, color: COLORS.TEXT_SECONDARY, lineHeight: 1.3 }}>
                    {details.designation || "-"}
                  </Typography>
                </Box>
              </FlexCell>

              <FlexCell flex={1.2}>
                <Typography sx={{ ...TYPOGRAPHY.BODY_LARGE, color: COLORS.SECONDARY }}>
                  {details.purposeOfVisit || "-"}
                </Typography>
              </FlexCell>

              <FlexCell flex={1.5}>
                <Typography sx={{ ...TYPOGRAPHY.BODY_LARGE, color: COLORS.SECONDARY }}>
                  {details.instructionsDecisions || "-"}
                </Typography>
              </FlexCell>

              <FlexCell w="90px">
                <Typography sx={{ ...TYPOGRAPHY.BODY_LARGE, color: COLORS.SECONDARY }}>
                  {details.timeIn || "-"}
                </Typography>
              </FlexCell>

              <FlexCell w="90px">
                <Typography sx={{ ...TYPOGRAPHY.BODY_LARGE, color: COLORS.SECONDARY }}>
                  {details.timeOut || "-"}
                </Typography>
              </FlexCell>

              <FlexCell flex={1.5} sx={{
                maxWidth: '250px',
                wordWrap: 'break-word',
                overflowWrap: 'break-word',
                whiteSpace: 'normal'
              }}>
                <Typography sx={{
                  ...TYPOGRAPHY.BODY_LARGE,
                  color: COLORS.SECONDARY,
                  wordBreak: 'break-word',
                  whiteSpace: 'pre-wrap'
                }}>
                  {details.remarks || row.adminRemarks || "-"}
                </Typography>
              </FlexCell>
            </FlexRow>
          );
        })}
      </Box>
    );
  };

  // ========================= DELAYS & HINDRANCES SECTION =========================
  const renderDelaysHindrancesSection = () => {
    const data = groupedDPRData["Delay Hindrances"] || [];
    if (!hasValidData(data, "delaysHindrances")) return null;

    return (
      <Box mt={4}>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
          <Typography sx={{ ...TYPOGRAPHY.H3, color: COLORS.TEXT, mb: 1 }}>
            Delays & Hindrances
          </Typography>
        </Box>

        {/* HEADER */}
        <FlexRow sx={{ borderBottom: `1px solid ${COLORS.LIGHT_SECONDARY}`, pb: 1 }}>
          <FlexCell w="40px">
            <Typography sx={{ ...TYPOGRAPHY.H4, color: COLORS.TEXT }}>Sr</Typography>
          </FlexCell>
          <FlexCell flex={2}>
            <Typography sx={{ ...TYPOGRAPHY.H4, color: COLORS.TEXT }}>Description of Delay</Typography>
          </FlexCell>
          <FlexCell w="120px">
            <Typography sx={{ ...TYPOGRAPHY.H4, color: COLORS.TEXT }}>Duration</Typography>
          </FlexCell>
          <FlexCell flex={1.5}>
            <Typography sx={{ ...TYPOGRAPHY.H4, color: COLORS.TEXT }}>Reason</Typography>
          </FlexCell>
          <FlexCell flex={2}>
            <Typography sx={{ ...TYPOGRAPHY.H4, color: COLORS.TEXT }}>Remark</Typography>
          </FlexCell>
        </FlexRow>

        {/* ROWS */}
        {data.map((row, i) => {
          const details = row.delayHindrancesDetails || {};

          return (
            <FlexRow sx={{ borderBottom: `1px solid ${COLORS.LIGHT_SECONDARY}`, py: 1 }} key={row._id}>
              <FlexCell w="40px">
                <Typography sx={{ ...TYPOGRAPHY.BODY_LARGE, color: COLORS.SECONDARY }}>
                  {i + 1}
                </Typography>
              </FlexCell>

              <FlexCell flex={2}>
                <Typography sx={{ ...TYPOGRAPHY.BODY_LARGE, color: COLORS.SECONDARY }}>
                  {details.descriptionOfDelay || "-"}
                </Typography>
              </FlexCell>

              <FlexCell w="120px">
                <Typography sx={{ ...TYPOGRAPHY.BODY_LARGE, color: COLORS.SECONDARY }}>
                  {details.duration || "-"}
                </Typography>
              </FlexCell>

              <FlexCell flex={1.5}>
                <Typography sx={{ ...TYPOGRAPHY.BODY_LARGE, color: COLORS.SECONDARY }}>
                  {details.reason || "-"}
                </Typography>
              </FlexCell>

              <FlexCell flex={2}>
                <Typography sx={{ ...TYPOGRAPHY.BODY_LARGE, color: COLORS.SECONDARY }}>
                  {details.remarks || row.adminRemarks || "-"}
                </Typography>
              </FlexCell>
            </FlexRow>
          );
        })}
      </Box>
    );
  };

  // ========================= PHOTOGRAPHS SECTION =========================
  const renderPhotographsSection = () => {
    const data = groupedDPRData["Photograph"] || [];
    if (!hasValidData(data, "photographs")) return null;

    return (
      <Box mt={4}>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={1.5}>
          <Typography sx={{ ...TYPOGRAPHY.H3, color: COLORS.TEXT }}>
            Photographs
          </Typography>
        </Box>

        {/* PHOTOS - 3 per row */}
        <Box sx={{ 
          display: "grid", 
          gridTemplateColumns: { xs: "1fr", sm: "repeat(2, 1fr)", md: "repeat(3, 1fr)" },
          gap: 3 
        }}>
          {data.map((item, index) => {
            const details = item.photographDetails || {};
            const photoSrc = createImageUrl(details.photoPath);

            return (
              <Box key={item._id}>
                {/* Image */}
                <img 
                  src={photoSrc} 
                  alt={details.activityLocation || "DPR Photo"}
                  style={{ 
                    width: "100%", 
                    height: "180px", 
                    objectFit: "contain",
                    display: "block",
                    marginBottom: "10px",
                    border: `1px solid ${COLORS.LIGHT_SECONDARY}`,
                    borderRadius: "4px"
                  }}
                  onError={(e) => {
                    e.target.onerror = null;
                    e.target.src = "https://via.placeholder.com/300x200?text=Image+Not+Found";
                  }}
                />
                
                {/* Details */}
                <Box>
                  <Box sx={{ mb: 1.5 }}>
                    <Typography sx={{
                      ...TYPOGRAPHY.BODY_SMALL,
                      color: COLORS.PRIMARY,
                      fontWeight: 600,
                      mb: 0.3
                    }}>
                      Activity / Location:
                    </Typography>
                    <Typography sx={{
                      ...TYPOGRAPHY.BODY_SMALL,
                      color: COLORS.PRIMARY,
                      wordWrap: "break-word",
                      whiteSpace: "normal",
                      lineHeight: 1.4
                    }}>
                      {details.activityLocation || "-"}
                    </Typography>
                  </Box>

                  <Box sx={{ mb: 1.5 }}>
                    <Typography sx={{
                      ...TYPOGRAPHY.BODY_SMALL,
                      color: COLORS.PRIMARY,
                      fontWeight: 600,
                      mb: 0.3
                    }}>
                      Photo Reference No.:
                    </Typography>
                    <Typography sx={{
                      ...TYPOGRAPHY.BODY_SMALL,
                      color: COLORS.PRIMARY,
                      wordWrap: "break-word",
                      whiteSpace: "normal",
                      lineHeight: 1.4
                    }}>
                      {details.photoReferenceNo || "-"}
                    </Typography>
                  </Box>

                  <Box sx={{ mb: 1.5 }}>
                    <Typography sx={{
                      ...TYPOGRAPHY.BODY_SMALL,
                      color: COLORS.PRIMARY,
                      fontWeight: 600,
                      mb: 0.3
                    }}>
                      Remark:
                    </Typography>
                    <Typography sx={{
                      ...TYPOGRAPHY.BODY_SMALL,
                      color: COLORS.PRIMARY,
                      wordWrap: "break-word",
                      whiteSpace: "normal",
                      lineHeight: 1.4
                    }}>
                      {details.remarks || item.adminRemarks || "-"}
                    </Typography>
                  </Box>
                </Box>
              </Box>
            );
          })}
        </Box>
      </Box>
    );
  };

  // Filter sections that have valid data
  const validSections = sections.filter(section =>
    hasValidData(section.data, section.dataType)
  );

  if (loading) {
    return (
      <Container maxWidth="xl" disableGutters sx={{ p: 1.2 }}>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  if (error) {
    return (
      <Container maxWidth="xl" disableGutters sx={{ p: 1.2 }}>
        <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>
        <Button variant="contained" onClick={() => navigate(-1)}>
          Go Back
        </Button>
      </Container>
    );
  }

  return (
    <Container maxWidth="xl" disableGutters sx={{ p: 1.2 }}>
      {/* Toast Notification */}
      <AppToast
        open={toast.open}
        type={toast.type}
        title={toast.title}
        message={toast.message}
        onClose={closeToast}
      />

      {/* Header */}
      <Box sx={{ mb: 2 }}>
        {/* Row 1 */}
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          mb={1}
        >
          {/* LEFT: TITLE */}
          <Box display="flex" alignItems="center" gap={1.5}>
            <Typography sx={{ ...TYPOGRAPHY.H2, color: COLORS.PRIMARY }}>
              Daily Progress Report (DPR)
            </Typography>
          </Box>

          {/* RIGHT: EDIT REPORT + DOWNLOAD + CLOSE */}
          <Box display="flex" alignItems="center" gap={1}>
            {/* Edit Report Button - Conditionally Visible */}
            {shouldShowEditIcon() && (
              <Button
                onClick={handleEditDPR}
                startIcon={<Icon name="edit" />}
                sx={{
                  backgroundColor: COLORS.SECONDARY,
                  color: "#fff",
                  textTransform: "none",
                  borderRadius: "7px",
                  px: 2,
                  height: 36,
                  "&:hover": {
                    backgroundColor: COLORS.PRIMARY,
                  },
                }}
              >
                Edit Report
              </Button>
            )}

            <Button
              onClick={handleDownloadDPRReport}
              disabled={downloading}
              startIcon={
                downloading ? (
                  <CircularProgress size={16} color="inherit" />
                ) : (
                  <Icon name="download1" />
                )
              }
              sx={{
                backgroundColor: COLORS.SECONDARY,
                color: "#fff",
                textTransform: "none",
                borderRadius: "7px",
                px: 2,
                height: 36,
                "&:hover": {
                  backgroundColor: COLORS.PRIMARY,
                },
              }}
            >
              {downloading ? "Downloading..." : "Download PDF"}
            </Button>

            <IconButton size="small" onClick={() => navigate(-1)}>
              <Icon name="close3" color={COLORS.ERROR} />
            </IconButton>
          </Box>
        </Box>

        {/* Row 2 */}
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={0.5}>
          <Box display="flex" alignItems="center" gap={0.5}>
            <Typography sx={{ ...TYPOGRAPHY.H4, color: COLORS.TEXT }}>
              Project Name:
            </Typography>
            <Typography sx={{ ...TYPOGRAPHY.H4, color: COLORS.SECONDARY }}>
              {project?.projectName || "Hare Krishna Valley"}
            </Typography>
          </Box>

          <Box display="flex" alignItems="center" gap={1}>
            <Typography sx={{ ...TYPOGRAPHY.H4, color: COLORS.TEXT }}>
              Date:
            </Typography>

            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                border: `1px solid ${COLORS.LIGHT_SECONDARY}`,
                borderRadius: 1,
                px: 1.2,
                py: 0.3,
                gap: 0.6,
                fontSize: TYPOGRAPHY.BODY_SMALL.fontSize,
                color: COLORS.MUTED_TEXT,
                minWidth: 110,
                justifyContent: "space-between",
              }}
            >
              {formatDisplayDate(selectedDate)}
              <Icon name="calendar3" color={COLORS.LIGHT_SECONDARY} />
            </Box>
          </Box>
        </Box>

        {/* Row 3 */}
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={0.5}>
          <Box display="flex" alignItems="center" gap={0.5}>
            <Typography sx={{ ...TYPOGRAPHY.H4, color: COLORS.TEXT }}>
              Address:
            </Typography>
            <Typography sx={{ ...TYPOGRAPHY.H4, color: COLORS.SECONDARY }}>
              {project?.siteAddress || "Pune, Maharashtra"}
            </Typography>
          </Box>
          <Box />
        </Box>

        {/* Row 4 */}
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Box display="flex" alignItems="center" gap={0.5}>
            <Typography sx={{ ...TYPOGRAPHY.H4, color: COLORS.TEXT }}>
              Developer:
            </Typography>
            <Typography sx={{ ...TYPOGRAPHY.H4, color: COLORS.SECONDARY }}>
              {project?.developerName || "Vyankatesh Contracts"}
            </Typography>
          </Box>

          <Box display="flex" alignItems="center" gap={0.5}>
            <Typography sx={{ ...TYPOGRAPHY.H4, color: COLORS.TEXT }}>
              Engineer:
            </Typography>
            <Typography sx={{ ...TYPOGRAPHY.H4, color: COLORS.SECONDARY }}>
              {project?.assignedEngineer?.name || project?.assignedEngineerName || "Ashish Hase"}
            </Typography>
          </Box>
        </Box>

        {/* Divider */}
        <Box
          sx={{
            mt: 1.5,
            height: "1px",
            backgroundColor: COLORS.LIGHT_SECONDARY,
            opacity: 0.7,
          }}
        />
      </Box>

      {/* DPR Sections */}
      {validSections.length === 0 ? (
        <Alert severity="info" sx={{ mt: 3 }}>
          No DPR data available for this date.
        </Alert>
      ) : (
        <Box sx={{ 
          paddingTop: "5px", 
          paddingLeft: "15px", 
          paddingRight: "15px", 
          paddingBottom: "20px"
        }}>
          {renderWorkProgressSection()}
          {renderMaterialsSection()}
          {renderMachinerySection()}
          {renderQualityControlSection()}
          {renderSafetyObservationsSection()}
          {renderVisitorLogsSection()}
          {renderDelaysHindrancesSection()}
          {renderPhotographsSection()}
        </Box>
      )}

      {/* Footer Download Button */}
      {validSections.length > 0 && (
        <Box display="flex" justifyContent="center" mt={5} mb={5}>
          <Button
            onClick={handleDownloadDPRReport}
            disabled={downloading}
            sx={{
              backgroundColor: COLORS.SECONDARY,
              color: "#fff",
              textTransform: "uppercase",
              px: 5,
              py: 1.5,
              borderRadius: "8px",
              fontWeight: 600,
              "&:disabled": {
                backgroundColor: COLORS.LIGHT_SECONDARY,
              },
              "&:hover": {
                backgroundColor: COLORS.PRIMARY,
              }
            }}
          >
            {downloading ? (
              <>
                <CircularProgress size={20} sx={{ color: "#fff", mr: 1 }} />
                Downloading DPR...
              </>
            ) : "Download Full DPR Report"}
          </Button>
        </Box>
      )}
    </Container>
  );
};

/* ========================= FLEX HELPERS ========================= */

function FlexRow({ children, sx }) {
  return (
    <Box
      display="flex"
      alignItems="center"
      py={1}
      borderBottom={`1px solid ${COLORS.LIGHT_SECONDARY}`}
      sx={sx}
    >
      {children}
    </Box>
  );
}

function FlexCell({ children, flex, w, sx }) {
  return (
    <Box
      sx={{
        flex: flex || "none",
        width: w,
        fontSize: TYPOGRAPHY.BODY_STANDARD.fontSize,
        color: COLORS.TEXT,
        paddingLeft: sx?.pl || 0,
        ...sx,
      }}
    >
      {children}
    </Box>
  );
}

export default ViewDPR;